import {Form, Select} from 'antd';
import {useQuery} from '@apollo/client';
import moment from 'moment';
import PropTypes from 'prop-types';
import {GET_ANNOUNCEMENT_OPTIONS} from 'graphql/queries/announcement';

/**
 * Optional announcement reference on a notification.
 *
 * The notification service takes it as a top-level `announcementId` — the
 * spelling its `instrumentId` / `companyId` / `collectionId` siblings use —
 * and emits it to mobile as `metadata.announcementID`. The two casings are
 * deliberate; neither is a typo to be tidied up.
 *
 * It is optional on every notification type and never changes routing: the
 * notification still goes wherever its own type sends it. There is no
 * "open this announcement" destination type, so the copy here promises
 * nothing of the sort.
 */

/** Newest first, so the announcement someone just wrote is at the top. */
const byNewest = (a, b) =>
  moment(b?.publishedDate || 0).valueOf() -
  moment(a?.publishedDate || 0).valueOf();

const describe = announcement => {
  const published = announcement?.publishedDate
    ? moment(announcement.publishedDate).format('DD MMM, YYYY')
    : '';
  const name =
    announcement?.title ||
    announcement?.subTitle ||
    announcement?.type?.replaceAll('_', ' ') ||
    'Untitled announcement';
  return published ? `${name} — ${published}` : name;
};

export const RelatedAnnouncementPicker = ({className}) => {
  const {data, loading} = useQuery(GET_ANNOUNCEMENT_OPTIONS, {
    errorPolicy: 'all',
  });

  // An `Error` member of the union carries no `announcement`, so a failed
  // part of the query contributes nothing rather than breaking the picker.
  const readList = result => (result?.announcement || []).filter(Boolean);

  // Grouped rather than flattened: an expired announcement is still a valid
  // reference, but the author should see that that is what they picked.
  const groups = [
    {label: 'Active', items: readList(data?.getAllActiveAnnouncements)},
    {label: 'Upcoming', items: readList(data?.upcoming)},
    {label: 'Expired', items: readList(data?.expired)},
  ];

  const seen = [];
  const options = groups
    .map(group => ({
      label: group.label,
      options: group.items
        .filter(announcement => {
          if (!announcement.id || seen.includes(announcement.id)) return false;
          seen.push(announcement.id);
          return true;
        })
        .sort(byNewest)
        .map(announcement => ({
          value: announcement.id,
          label: describe(announcement),
        })),
    }))
    .filter(group => group.options.length);

  return (
    <Form.Item
      name="announcementId"
      label="Related announcement"
      className={className}
      extra="Optional. Passes the announcement along with the notification; it does not change where tapping the notification takes the reader.">
      {/* No fixed width: the redesigned sections size their own fields, and
          a hard 730px pushed this one past the card and scrolled the section
          sideways on a narrow window. */}
      <Select
        showSearch
        allowClear
        loading={loading}
        placeholder="Select an announcement"
        optionFilterProp="label"
        options={options}
      />
    </Form.Item>
  );
};

RelatedAnnouncementPicker.propTypes = {
  className: PropTypes.string,
};

RelatedAnnouncementPicker.defaultProps = {
  className: '',
};

export default RelatedAnnouncementPicker;
